import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useRef, useState } from "react";
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from "@xyflow/react";
import { useEdgeCallbacksRef, useEdgeWaypointActions } from "../contexts/edgeCallbacks";
import { buildSmoothStepPathThroughWaypoints, EditableEdgeControls, labelAnchorFromSegments, useBridgeEdge, useIsContextMenuActiveEdge, useInteractionState } from "../../../flow-kit";
// Maximum width for edge labels before text wrapping
const LABEL_MAX_WIDTH = 120;
// Distance in pixels to offset labels perpendicular to the edge direction
const LABEL_OFFSET_FROM_PATH = 12;
// Thickness of the invisible hover zones that track each segment of the edge
const HOVER_ZONE_THICKNESS = 24;
/** Value-equality for waypoint arrays — used to detect external waypoint
 * updates that should override our local render state vs. echoes of the
 * commit we just made (same content, new reference). */ function waypointsEqualByValue(a, b) {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for(let i = 0; i < a.length; i++){
        if (a[i].id !== b[i].id || a[i].x !== b[i].x || a[i].y !== b[i].y) {
            return false;
        }
    }
    return true;
}
/**
 * Simple hash function to generate a consistent numeric value from a string.
 * Used to create deterministic but varied offsets for edges from the same source.
 */ function hashString(str) {
    let hash = 0;
    for(let i = 0; i < str.length; i++){
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
}
/**
 * Compute the perpendicular offset for positioning a label relative to an edge.
 * The offset is always applied in the "upward" direction (negative Y in screen coords)
 * to keep labels above the edge path.
 */ function computeLabelOffset(dx, dy) {
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) {
        return {
            offsetX: 0,
            offsetY: -LABEL_OFFSET_FROM_PATH
        };
    }
    const perpX = -dy / len;
    const perpY = dx / len;
    const sign = perpY <= 0 ? 1 : -1;
    return {
        offsetX: perpX * LABEL_OFFSET_FROM_PATH * sign,
        offsetY: perpY * LABEL_OFFSET_FROM_PATH * sign
    };
}
function ProcessFlowEdge({ id, source, target, sourceHandleId, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, style, markerEnd, animated, data }) {
    const callbacksRef = useEdgeCallbacksRef();
    const waypointActionsRef = useEdgeWaypointActions();
    const isContextMenuActive = useIsContextMenuActiveEdge(source, target);
    const { isHovered, isActive, handleMouseEnter, handleMouseLeave } = useInteractionState({
        isContextMenuActive
    });
    const isHighlighted = isHovered || isActive;
    const edgeData = data;
    const isEdit = edgeData?.mode === "edit";
    const isOnPath = edgeData?.isOnPath === true;
    const savedWaypoints = edgeData?.waypoints ?? [];
    // Waypoints rendered on screen — local React state, mirroring the
    // node-drag pattern in `useEditableFlow` (see `prevStructural` /
    // `isDraggingRef`). Drag handlers mutate this eagerly so the path
    // follows the pointer AND stays at the dropped position across the
    // mutation round-trip. `savedWaypoints` only overrides it when the
    // parent changes the external value to something different (undo,
    // collaborator, load), detected via a render-time reference check
    // rather than a useEffect. Clearing on drag-stop (the old design)
    // caused a flash: the optimistic update awaits `cancelQueries` before
    // `setQueryData`, leaving one render where savedWaypoints was still
    // the pre-drag value.
    const [liveWaypoints, setLiveWaypoints] = useState(savedWaypoints);
    const lastSavedRef = useRef(savedWaypoints);
    if (lastSavedRef.current !== savedWaypoints) {
        lastSavedRef.current = savedWaypoints;
        if (!waypointsEqualByValue(savedWaypoints, liveWaypoints)) {
            setLiveWaypoints(savedWaypoints);
        }
    }
    const effectiveWaypoints = liveWaypoints;
    // Zero-waypoint fast path: keep the existing `getSmoothStepPath` branch so
    // edges that the user hasn't touched render pixel-identical to before.
    const hashVal = Math.abs(hashString(source));
    const bendRatio = 0.45 + hashVal % 100 / 1000;
    const centerX = sourceX + (targetX - sourceX) * bendRatio;
    const edgePath = effectiveWaypoints.length === 0 ? getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        centerX
    })[0] : buildSmoothStepPathThroughWaypoints({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        waypoints: effectiveWaypoints
    })[0];
    const { bridgePath, segments } = useBridgeEdge(id, edgePath);
    // Anchor the label to the rendered stroke via the segments `useBridgeEdge`
    // already parsed from `edgePath`, then nudge it just off the leg it labels
    // (up on horizontal legs, sideways on vertical ones). See
    // `labelAnchorFromSegments` for why the stroke — not the source→target chord
    // — is the right anchor for orthogonal, detour-prone edges.
    const { x: midX, y: midY, tangentX, tangentY } = labelAnchorFromSegments(segments, {
        sourceX,
        sourceY,
        targetX,
        targetY
    });
    const { offsetX, offsetY } = computeLabelOffset(tangentX, tangentY);
    const labelX = midX + offsetX;
    const labelY = midY + offsetY;
    const handleZoneContextMenu = useCallback((e)=>{
        // Don't preventDefault or stopPropagation: Radix's ContextMenuTrigger
        // sits on an ancestor and uses composeEventHandlers, which skips opening
        // the menu when event.defaultPrevented is true. Radix calls preventDefault
        // itself when it opens, so the browser's native menu still won't show.
        callbacksRef?.current?.onEdgeContextMenu(source, target, sourceHandleId ?? null, e.nativeEvent);
    }, [
        callbacksRef,
        source,
        target,
        sourceHandleId
    ]);
    const handleZoneClick = useCallback((e)=>{
        e.stopPropagation();
        callbacksRef?.current?.onEdgeClick(source, target, sourceHandleId ?? null, e.clientX, e.clientY);
    }, [
        callbacksRef,
        source,
        target,
        sourceHandleId
    ]);
    const handleWaypointsChange = useCallback((next)=>setLiveWaypoints(next), []);
    const handleWaypointsCommit = useCallback((next)=>{
        // Keep the dropped geometry as the render source-of-truth. The
        // render-time sync above will clear the override by advancing
        // `lastSavedRef` the moment `savedWaypoints` catches up (same-value
        // equality → no extra setState needed).
        setLiveWaypoints(next);
        waypointActionsRef?.current?.onCommit(source, target, sourceHandleId ?? null, next);
    }, [
        waypointActionsRef,
        source,
        target,
        sourceHandleId
    ]);
    const showWaypointControls = isEdit && isHighlighted && !!waypointActionsRef;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isOnPath && /*#__PURE__*/ _jsx("path", {
                d: bridgePath,
                fill: "none",
                stroke: "#3B82F6",
                strokeWidth: 12,
                strokeOpacity: 0.25,
                strokeLinecap: "round"
            }),
            isEdit && isHighlighted && !isOnPath && /*#__PURE__*/ _jsx("path", {
                d: bridgePath,
                fill: "none",
                stroke: "#3B82F6",
                strokeWidth: 8,
                strokeOpacity: 0.15,
                strokeLinecap: "round",
                style: {
                    pointerEvents: "none"
                }
            }),
            /*#__PURE__*/ _jsx(BaseEdge, {
                id: id,
                path: bridgePath,
                style: {
                    ...style,
                    ...isEdit && isHighlighted ? {
                        stroke: "#3B82F6",
                        strokeWidth: 3
                    } : {}
                },
                markerEnd: markerEnd,
                className: animated ? "animated" : ""
            }),
            isEdit && /*#__PURE__*/ _jsx("path", {
                d: bridgePath,
                fill: "none",
                stroke: "transparent",
                strokeWidth: HOVER_ZONE_THICKNESS,
                className: "nodrag nopan",
                style: {
                    pointerEvents: "stroke",
                    cursor: "pointer"
                },
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                onClick: handleZoneClick,
                onContextMenu: handleZoneContextMenu
            }),
            showWaypointControls && /*#__PURE__*/ _jsx(EditableEdgeControls, {
                sourceX: sourceX,
                sourceY: sourceY,
                sourcePosition: sourcePosition,
                targetX: targetX,
                targetY: targetY,
                targetPosition: targetPosition,
                centerX: centerX,
                waypoints: effectiveWaypoints,
                onChange: handleWaypointsChange,
                onCommit: handleWaypointsCommit,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave
            }),
            label && /*#__PURE__*/ _jsx(EdgeLabelRenderer, {
                children: /*#__PURE__*/ _jsx("div", {
                    style: {
                        position: "absolute",
                        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                        pointerEvents: "all",
                        zIndex: isOnPath ? 1001 : 1000,
                        maxWidth: LABEL_MAX_WIDTH,
                        fontSize: 10,
                        fontWeight: isOnPath ? 600 : 500,
                        color: isOnPath ? "#1D4ED8" : "#374151",
                        backgroundColor: isOnPath ? "rgba(219, 234, 254, 0.98)" : "rgba(255, 255, 255, 0.95)",
                        padding: "2px 6px",
                        borderRadius: 4,
                        border: isOnPath ? "1px solid #3B82F6" : "1px solid #E5E7EB",
                        textAlign: "center",
                        lineHeight: 1.3,
                        wordBreak: "break-word"
                    },
                    className: "nodrag nopan",
                    children: label
                })
            })
        ]
    });
}
export default /*#__PURE__*/ memo(ProcessFlowEdge);

//# sourceMappingURL=FlowEdge.js.map